import _defineProperty from "@babel/runtime/helpers/defineProperty";
function ownKeys(e, r) { var t = Object.keys(e); if (Object.getOwnPropertySymbols) { var o = Object.getOwnPropertySymbols(e); r && (o = o.filter(function (r) { return Object.getOwnPropertyDescriptor(e, r).enumerable; })), t.push.apply(t, o); } return t; }
function _objectSpread(e) { for (var r = 1; r < arguments.length; r++) { var t = null != arguments[r] ? arguments[r] : {}; r % 2 ? ownKeys(Object(t), !0).forEach(function (r) { _defineProperty(e, r, t[r]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(e, Object.getOwnPropertyDescriptors(t)) : ownKeys(Object(t)).forEach(function (r) { Object.defineProperty(e, r, Object.getOwnPropertyDescriptor(t, r)); }); } return e; }
import { memo, useRef, useEffect, useMemo, useCallback } from "react";
import { compose, sortWith, descend, prop, groupBy, pick, keys, propEq, equals } from "ramda";
import * as d3 from "d3";
import { getColorUsingOption } from "utils/colors";
import { ellipsis } from "utils/format";
import { valueBearing } from "utils/validation";
import { extractValue } from "utils/measurement";
import { getPeriod, collectPeriodsBetween, periodsBetween } from "utils/periods";
import { targetColor } from "utils/colors";
import { sortByPeriod } from "utils/sort";
import { getPaddedMax, getPaddedMin } from "@dataVisualizations/dataVizualizations.utils";
import { initGraph, updateLines, updateXAxis, updateYAxis, updateGridLines, updateTimePeriodSurfaces, updateSoughtValue, updateDots } from "./renderFunctions";
import useGraphRender from "@dataVisualizations/hooks/useGraphRender";
import { measurementsParams } from "utils/measurementsWithMetadata";
import { useUnitListsContext } from "@unitLists";
import { jsx as _jsx } from "react/jsx-runtime";
var defaultMetadata = {
  measureUnit: "%",
  targetMeasurements: [{
    value: 90
  }],
  soughtValue: "Högt"
};
export default /*#__PURE__*/memo(function TimelineGraph(props) {
  var query = props.query,
    measure = props.measure,
    measurementsWithMetadata = props.measurementsWithMetadata,
    useContainerHeight = props.useContainerHeight,
    disableGraphInteractions = props.disableGraphInteractions,
    focusId = props.focusId,
    prevFocusId = props.prevFocusId,
    setFocusId = props.setFocusId,
    containerRef = props.containerRef,
    changeToSingleDateAndBarchart = props.changeToSingleDateAndBarchart,
    lookupOrganization = props.lookupOrganization,
    hideYAxis = props.hideYAxis,
    capYTicks = props.capYTicks;
  var graphRef = useRef();
  var description = useRef();
  var hydratedTargets = useRef();
  var _useUnitListsContext = useUnitListsContext(),
    activeUnitList = _useUnitListsContext.activeUnitList;
  var multipleGenders = measurementsWithMetadata && compose(function (x) {
    return x.length === 2;
  }, keys, groupBy(prop("measureId")))(measurementsWithMetadata);
  var margin = useMemo(function () {
    return props.margin ? props.margin : {
      left: 15,
      right: 60,
      bottom: 85,
      top: 20
    };
  }, [props.margin]);
  var setDescription = useCallback(function (value, color, org, x, y, dateLabel, gender) {
    var genderLabel = gender && "".concat(gender, ": ") || "";
    description.current.selectAll("rect").attr("stroke", color);
    description.current.select(".value").text(ellipsis(21, genderLabel + value));
    if (dateLabel) {
      description.current.select(".value").style("font-size", "14px").style("font-family", "Roboto-regular").attr("transform", "translate(12, 20)");
      description.current.select(".date").text(dateLabel).style("font-family", "Roboto-regular");
    } else {
      description.current.select(".date").text("").style("font-family", "Roboto-regular");
      description.current.select(".value").style("font-size", "18px").style("font-family", "Roboto-bold").style("font-weight", "normal").attr("transform", "translate(12, 30)");
    }
    description.current.select(".organization").text(ellipsis(27, org)).style("font-family", "Roboto-regular");
    var width = 220;
    var top = y < margin.top / 2 + 54 ? margin.top / 1.5 : y - 80 + margin.top;
    var edgeLeft = margin.left / 2;
    var edgeRight = containerRef.current.offsetWidth - width - margin.right / 2;
    var max = Math.max(edgeLeft, x - width / 2 + margin.left);
    var left = Math.min(max, edgeRight);
    var translation = "translate(".concat(left, ",").concat(top, ")");
    if (description.current.attr("transform") === "translate(0,0)") description.current.attr("transform", translation);
    description.current.transition().attr("transform", translation).attr("opacity", 1);
  }, [containerRef, margin.top, margin.left, margin.right]);
  var setSvgHeight = useCallback(function (svg) {
    if (useContainerHeight) return containerRef.current.offsetHeight;
    var totalHeight;
    var rowHeight = multipleGenders ? 38 : 34;
    var minHeight = rowHeight * 10 + margin.bottom + margin.top;
    var maxHeight = rowHeight * 23 + margin.bottom + margin.top;
    totalHeight = query.units.length * rowHeight + margin.bottom + margin.top;
    totalHeight = totalHeight < minHeight ? minHeight : totalHeight > maxHeight ? maxHeight : totalHeight;
    svg.style("height", "".concat(totalHeight, "px"));
    return totalHeight;
  }, [containerRef, margin.bottom, margin.top, query.units, multipleGenders, useContainerHeight]);
  var removeTargetHover = useCallback(function () {
    d3.selectAll(".hover-description-group").selectAll(".manymeasure-target-value").attr("stroke-width", 3).attr("stroke-dasharray", "3,3").transition().attr("opacity", 1);
    description.current.transition().attr("opacity", 0);
  }, []);
  var addTargetHover = useCallback(function () {
    var idx = hydratedTargets.current.findIndex(function (x) {
      return x.Id === props.focusId;
    });
    var target = hydratedTargets.current[idx];
    d3.selectAll(".hover-description-group").selectAll(".manymeasure-target-value").transition().attr("opacity", 0.1);
    d3.selectAll(".hover-description-group").selectAll(".target".concat(idx)).attr("stroke-width", 5).attr("stroke-dasharray", "6,6").transition().attr("opacity", 1);
    setDescription(target.Value, targetColor(idx), target.TargetName, 5000, target.scaleY);
  }, [props.focusId, setDescription]);
  var addGenderHover = useCallback(function () {
    var gender = function gender(d) {
      var _measure$submeasures$;
      return (_measure$submeasures$ = measure.submeasures.find(propEq("measureId", d.measureId))) === null || _measure$submeasures$ === void 0 ? void 0 : _measure$submeasures$.gender;
    };
    function opacitySelector(d) {
      return focusId === gender(d) ? "line" : "line dim-line";
    }
    function opacitySelectorCircles(d) {
      return focusId === gender(d) ? "circleGroup" : "circleGroup dim-line";
    }
    var svg = d3.select(graphRef.current);
    svg.select(".chartData").selectAll("path").attr("class", opacitySelector);
    svg.select(".chartData").selectAll(".circleGroup").attr("class", opacitySelectorCircles);
  }, [focusId, measure.submeasures]);
  var removeGenderHover = function removeGenderHover() {
    var svg = d3.select(graphRef.current);
    svg.select(".chartData").selectAll("path").attr("class", "line");
    svg.select(".chartData").selectAll(".circleGroup").attr("class", "circleGroup");
  };
  useEffect(function () {
    description.current = initGraph(graphRef.current, setSvgHeight, margin);
  }, [margin, setSvgHeight]);
  useEffect(function () {
    var _hydratedTargets$curr, _hydratedTargets$curr2;
    var previousWasTarget = (_hydratedTargets$curr = hydratedTargets.current) === null || _hydratedTargets$curr === void 0 ? void 0 : _hydratedTargets$curr.find(function (x) {
      return x.Id === prevFocusId;
    });
    var currentIsTarget = (_hydratedTargets$curr2 = hydratedTargets.current) === null || _hydratedTargets$curr2 === void 0 ? void 0 : _hydratedTargets$curr2.find(function (x) {
      return x.Id === focusId;
    });
    var previousWasGender = ["Male", "Female"].includes(prevFocusId);
    var currentIsGender = ["Male", "Female"].includes(focusId);
    if (previousWasTarget && !currentIsTarget) removeTargetHover();
    if (currentIsTarget) addTargetHover();
    if (previousWasGender && !currentIsGender) removeGenderHover();
    if (currentIsGender) addGenderHover();
  }, [focusId, prevFocusId, removeTargetHover, addGenderHover, addTargetHover]);
  var renderGraph = function renderGraph() {
    var _ref = measure || defaultMetadata,
      measureUnit = _ref.measureUnit,
      targetMeasurements = _ref.targetMeasurements;
    var targetValues = (targetMeasurements === null || targetMeasurements === void 0 ? void 0 : targetMeasurements.map(prop("Value"))) || [];
    var unitlistIdx = function unitlistIdx(id) {
      return query.units.indexOf(id);
    };
    var hydratedMeasurementsWithMetadata = (measurementsWithMetadata === null || measurementsWithMetadata === void 0 ? void 0 : measurementsWithMetadata.map(function (d) {
      return _objectSpread(_objectSpread({}, d), {}, {
        periodType: query.periodType,
        measurements: sortWith([descend(sortByPeriod)], d.measurements)
      });
    })) || [];
    var svg = d3.select(graphRef.current).attr("width", containerRef.current.offsetWidth).attr("height", 34 + margin.top + margin.bottom);
    var svgHeight = setSvgHeight(svg);
    svg.attr("width", containerRef.current.offsetWidth).attr("height", svgHeight);
    var contentWidth = containerRef.current.offsetWidth - margin.left - margin.right;
    var contentHeight = svgHeight - margin.top - margin.bottom;
    var periodFrom = query.periodType && query.dateFrom && getPeriod(query.periodType, query.dateFrom);
    var periodTo = query.periodType && query.dateTo && getPeriod(query.periodType, query.dateTo);
    var measurementPeriods = query.periodType && periodFrom && periodTo && collectPeriodsBetween(query.periodType, periodFrom, periodTo) || [];
    var ticks = periodsBetween(query.periodType, query.dateFrom, query.dateTo);
    var cappedTicks = Math.min(ticks, Math.round(contentWidth / 40));
    var lineStyles = {
      Male: new Array(1000).fill(5),
      Female: []
    };
    var key = function key(d) {
      return "".concat(d.measureId, "-").concat(d.organizationId, "-").concat(d.periodType);
    };
    var inverse = function inverse(d) {
      return d * -1;
    };
    var totalLength = function totalLength(d, idx, e) {
      return e && e[idx] && e[idx].getTotalLength && e[idx].getTotalLength() || 0;
    };
    var pathDecoration = function pathDecoration(d, idx, e) {
      return lineStyles[measure.submeasures.find(propEq("measureId", d.measureId)).gender || "Female"].join(",") + totalLength(d, idx, e);
    };
    var genderDash = function genderDash(d, idx, e) {
      return multipleGenders ? pathDecoration(d, idx, e) : totalLength(d, idx, e);
    };
    var end = function end(d) {
      return d.period.end;
    };
    var xScale = d3.scaleUtc().domain([periodFrom === null || periodFrom === void 0 ? void 0 : periodFrom.end, periodTo === null || periodTo === void 0 ? void 0 : periodTo.end]).range([0, contentWidth]);
    var allValuesFlat = hydratedMeasurementsWithMetadata.flatMap(function (x) {
      return x.measurements;
    }).map(extractValue(null));
    var withTargets = allValuesFlat ? targetValues ? allValuesFlat.concat(targetValues) : allValuesFlat : targetValues ? targetValues : [];
    var minValue = query.zoom && getPaddedMin(withTargets);
    minValue = typeof minValue === "number" ? minValue : 0;
    var maxValue = measureUnit && measureUnit.indexOf("%") !== -1 && !query.zoom ? 100 : getPaddedMax(withTargets);
    maxValue = typeof maxValue === "number" ? measureUnit && measureUnit.indexOf("%") !== -1 && maxValue > 100 ? 100 : maxValue : 100;
    var yScale = d3.scaleLinear().domain([minValue, maxValue]).nice().range([contentHeight, 0]);
    var idxMultiplier = multipleGenders ? function (idx) {
      return Math.floor(idx / 2);
    } : function (idx) {
      return idx;
    };
    var color = getColorUsingOption(query.colorSettings, activeUnitList);
    var valueline = d3.line().defined(compose(valueBearing, extractValue(false))).x(compose(xScale, end)).y(compose(yScale, extractValue(null)));
    var content = svg.select(".chartData");
    var hydrateScale = function hydrateScale(x) {
      return _objectSpread(_objectSpread({}, x), {}, {
        scaleY: yScale(x.Value)
      });
    };
    hydratedTargets.current = (targetMeasurements || []).slice().sort(function (a, b) {
      return (b.Value || b.value) - (a.Value || a.value);
    }).map(hydrateScale);
    var config = _objectSpread(_objectSpread({
      svg: svg,
      content: content,
      measurementsWithMetadata: hydratedMeasurementsWithMetadata,
      key: key,
      margin: margin,
      color: color,
      idxMultiplier: idxMultiplier,
      xScale: xScale,
      yScale: yScale,
      valueline: valueline,
      inverse: inverse,
      disableGraphInteractions: disableGraphInteractions,
      changeToSingleDateAndBarchart: changeToSingleDateAndBarchart,
      description: description.current,
      setDescription: setDescription,
      ie11SafeLength: totalLength,
      genderDash: genderDash,
      cappedTicks: cappedTicks
    }, pick(["periodType", "dateFrom", "dateTo"], query)), {}, {
      target: query.showTarget,
      width: contentWidth,
      svgWidth: containerRef.current.offsetWidth,
      metadata: measure || defaultMetadata,
      measurementPeriods: measurementPeriods,
      unitlistIdx: unitlistIdx,
      organizationLookup: lookupOrganization,
      hideYAxis: hideYAxis,
      capYTicks: capYTicks,
      setFocusId: setFocusId,
      hydratedTargets: hydratedTargets.current
    });
    updateXAxis(config);
    updateYAxis(config);
    updateGridLines(config);
    updateTimePeriodSurfaces(config);
    updateSoughtValue(config);
    updateLines(config);
    updateDots(config);
    d3.selectAll(".tick").selectAll("line").style("stroke-width", "1px").style("stroke", "#e8e7e6");
    d3.selectAll(".line").style("fill", "none").style("stroke-width", "2px");
  };
  useGraphRender(_objectSpread(_objectSpread(_objectSpread({}, measurementsParams(query)), pick(["zoom", "colorSettings", "sort", "relativeTo", "showTarget", "focusedUnit"], query)), {}, {
    measure: measure,
    measurementsWithMetadata: measurementsWithMetadata,
    activeUnitList: activeUnitList,
    lookupOrganization: lookupOrganization
  }), containerRef, renderGraph);
  return /*#__PURE__*/_jsx("svg", {
    ref: graphRef,
    className: "svg_content"
  });
}, function (prev, curr) {
  return equals(prev.query, curr.query) && equals(prev.loading, curr.loading) && equals(prev.measurementsWithMetadata, curr.measurementsWithMetadata) && equals(prev.measure, curr.measure) && equals(prev.activeUnitList, curr.activeUnitList) && equals(prev.focusId, curr.focusId) && equals(prev.lookupOrganization, curr.lookupOrganization);
});